import { useState, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { darkTheme } from '../../lib/tokens';
import {
  fetchFeedback,
  fetchFeedbackDetail,
  updateFeedback,
  type FeedbackFilters,
  type FeedbackUpdate,
} from './api';
import { useAppContextStore } from '../app/appContextStore';
import { ALL_APPS, DEFAULT_APP_ID, appLabel } from '../../lib/appConstants';
import type {
  FeedbackCategory,
  FeedbackListItem,
  FeedbackSource,
  FeedbackStatus,
} from './types';
import { useCanWrite } from '../auth/permissions';
import { FeedbackReplyPanel } from './FeedbackReplyPanel';

const PAGE_SIZE = 50;

const STATUS_META: Record<FeedbackStatus, { label: string; color: string; bg: string }> = {
  new: { label: 'Neu', color: darkTheme.colors.accentLight, bg: darkTheme.colors.accentSubtle },
  triaged: { label: 'Triagiert', color: darkTheme.colors.info, bg: darkTheme.colors.infoBg },
  resolved: { label: 'Erledigt', color: darkTheme.colors.success, bg: darkTheme.colors.successBg },
  wontfix: { label: "Won't Fix", color: darkTheme.colors.textTertiary, bg: darkTheme.colors.bgElevated },
};

const SOURCE_LABEL: Record<FeedbackSource, string> = {
  WEBSITE: 'Website',
  APP: 'App',
};

const CATEGORY_LABEL: Record<FeedbackCategory, string> = {
  GENERAL: 'Allgemein',
  BUG: 'Bug',
  FEATURE_REQUEST: 'Feature',
  CONTENT: 'Inhalt',
  UX: 'UX',
  PRAISE: 'Lob',
  SUPPORT: 'Support',
};

// ── Beta-Feedback-Context (strukturierte Umfrage) ─────────
// Rendert das `context`-Objekt des "beta-feedback-page"-Triggers lesbar
// statt als roher JSON-Dump.

const AREA_LABELS: Record<string, string> = {
  onboarding: 'Onboarding',
  dashboard: 'Startseite',
  curriculum: 'Atlas / Curriculum',
  lessons_read: 'Lektionen — Lesen',
  lessons_audio: 'Lektionen — Audio',
  flashcards: 'Karteikarten',
  quiz: 'Quiz / Prüfungsfragen',
  self_test: 'Selbsttest',
  oral_exam: 'Mündliche Prüfung',
  ai_tutor: 'KI-Tutor',
  glossary: 'Glossar',
  plan: 'Wochenplan',
};

const GLOBAL_LABELS: Record<string, string> = {
  ui: 'Design / UI',
  speed: 'Geschwindigkeit',
  stability: 'Stabilität',
  structure: 'Struktur',
};

const FREETEXT_LABELS: Record<string, string> = {
  likes: 'Gefällt',
  dislikes: 'Gefällt nicht',
  bugs: 'Bugs & Fehler',
  other: 'Wünsche & Sonstiges',
};

const PERSONA_LABELS: Record<string, string> = {
  anna: 'Anna',
  jan: 'Jan',
  weber: 'Dr. Weber',
};

interface PersonaVoice {
  rating?: number | null;
  comment?: string | null;
  notHeard?: boolean;
}

interface BetaFeedbackShape {
  page?: string | null;
  freeText?: Record<string, string | null> | null;
  referrer?: string | null;
  userAgent?: string | null;
  testerCode?: string | null;
  areaRatings?: Record<string, number | null> | null;
  globalRatings?: Record<string, number | null> | null;
  personaVoices?: Record<string, PersonaVoice | null> | null;
}

function isBetaFeedback(ctx: Record<string, unknown>): boolean {
  return (
    'areaRatings' in ctx ||
    'globalRatings' in ctx ||
    'personaVoices' in ctx ||
    'freeText' in ctx
  );
}

function ratingColor(n: number): string {
  if (n <= 2) return darkTheme.colors.danger;
  if (n === 3) return darkTheme.colors.warning;
  return darkTheme.colors.success;
}

function Stars({ value, max = 5 }: { value: number; max?: number }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span style={{ letterSpacing: '1px' }}>
        <span style={{ color: ratingColor(value) }}>{'★'.repeat(value)}</span>
        <span style={{ color: darkTheme.colors.borderStrong }}>{'★'.repeat(Math.max(0, max - value))}</span>
      </span>
      <span className="text-[10px] text-text-tertiary">
        {value}/{max}
      </span>
    </span>
  );
}

function BetaSubHeader({ children }: { children: string }): JSX.Element {
  return (
    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
      {children}
    </div>
  );
}

function BetaFeedbackContext({ ctx }: { ctx: BetaFeedbackShape }): JSX.Element {
  const c = darkTheme.colors;
  const freeText = ctx.freeText ?? {};

  const freeTextEntries = (Object.keys(FREETEXT_LABELS) as string[])
    .filter((key) => (freeText[key] ?? '').trim().length > 0)
    .map((key) => [key, FREETEXT_LABELS[key]] as const);

  const globalEntries = Object.entries(ctx.globalRatings ?? {}).filter(
    (entry): entry is [string, number] => typeof entry[1] === 'number',
  );

  const areaEntries = Object.entries(ctx.areaRatings ?? {})
    .filter((entry): entry is [string, number] => typeof entry[1] === 'number')
    // Problembereiche (niedrige Bewertung) zuerst
    .sort((a, b) => a[1] - b[1]);

  const personaEntries = Object.entries(ctx.personaVoices ?? {}).filter(
    (entry): entry is [string, PersonaVoice] => entry[1] != null,
  );

  return (
    <div className="space-y-4">
      {/* Freitext */}
      {freeTextEntries.length > 0 && (
        <div className="space-y-2">
          {freeTextEntries.map(([key, label]) => {
            const accent =
              key === 'bugs' || key === 'dislikes'
                ? c.danger
                : key === 'likes'
                  ? c.success
                  : c.textTertiary;
            return (
              <div key={key} className="rounded-md p-2.5" style={{ background: c.bgSurface }}>
                <div
                  className="mb-1 text-[10px] font-semibold uppercase tracking-wide"
                  style={{ color: accent }}
                >
                  {label}
                </div>
                <p
                  className="text-sm leading-relaxed text-text-primary"
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {freeText[key]}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Gesamt-Eindruck */}
      {globalEntries.length > 0 && (
        <div>
          <BetaSubHeader>Gesamt-Eindruck</BetaSubHeader>
          <div className="flex flex-wrap gap-2">
            {globalEntries.map(([key, v]) => (
              <div
                key={key}
                className="flex items-center gap-2 rounded-md px-2.5 py-1.5"
                style={{ background: c.bgSurface }}
              >
                <span className="text-xs text-text-secondary">{GLOBAL_LABELS[key] ?? key}</span>
                <Stars value={v} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bereiche */}
      {areaEntries.length > 0 && (
        <div>
          <BetaSubHeader>Bereiche</BetaSubHeader>
          <div className="grid gap-1 sm:grid-cols-2">
            {areaEntries.map(([key, v]) => (
              <div
                key={key}
                className="flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5"
                style={{ background: c.bgSurface }}
              >
                <span className="text-xs text-text-secondary">{AREA_LABELS[key] ?? key}</span>
                <Stars value={v} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stimmen */}
      {personaEntries.length > 0 && (
        <div>
          <BetaSubHeader>Stimmen</BetaSubHeader>
          <div className="space-y-1.5">
            {personaEntries.map(([key, p]) => (
              <div key={key} className="rounded-md px-2.5 py-2" style={{ background: c.bgSurface }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-text-primary">
                    {PERSONA_LABELS[key] ?? key}
                  </span>
                  {p.notHeard ? (
                    <span className="text-[10px] text-text-tertiary">nicht gehört</span>
                  ) : typeof p.rating === 'number' ? (
                    <Stars value={p.rating} />
                  ) : null}
                </div>
                {(p.comment ?? '').trim() && (
                  <p className="mt-1 text-xs italic leading-relaxed text-text-secondary">
                    „{p.comment}“
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Meta */}
      {(ctx.page || ctx.testerCode || ctx.referrer || ctx.userAgent) && (
        <div className="flex flex-col gap-0.5 pt-1 text-[10px] text-text-tertiary">
          {ctx.page && <span>Seite: {ctx.page}</span>}
          {ctx.testerCode && <span>Tester-Code: {ctx.testerCode}</span>}
          {ctx.referrer && <span>Referrer: {ctx.referrer}</span>}
          {ctx.userAgent && <span className="break-all">User-Agent: {ctx.userAgent}</span>}
        </div>
      )}
    </div>
  );
}

export function FeedbackInboxPage(): JSX.Element {
  const [status, setStatus] = useState<FeedbackStatus | 'ALL'>('new');
  const [source, setSource] = useState<FeedbackSource | 'ALL'>('ALL');
  const [category, setCategory] = useState<FeedbackCategory | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Pro gewählter App (globaler Picker; „Alle Apps" → currivium). Feedback ist
  // pro App getaggt (App schickt ihren app-Code beim Absenden via JWT/X-App).
  const ctxApp = useAppContextStore((s) => s.appId);
  const app = ctxApp === ALL_APPS ? DEFAULT_APP_ID : ctxApp;

  const filters = useMemo<FeedbackFilters>(
    () => ({
      app,
      status: status === 'ALL' ? undefined : status,
      source: source === 'ALL' ? undefined : source,
      category: category === 'ALL' ? undefined : category,
      search: search.trim() || undefined,
      limit: PAGE_SIZE,
    }),
    [app, status, source, category, search],
  );

  const list = useQuery({
    queryKey: ['feedback', filters],
    queryFn: () => fetchFeedback(filters),
  });

  return (
    <div className="mx-auto max-w-[1400px]">
      <header className="mb-4 flex items-end justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-text-primary">
            Feedback-Inbox
            <span
              className="rounded-full px-2 py-0.5 text-[11px] font-normal"
              style={{
                background: darkTheme.colors.accentSubtle,
                color: darkTheme.colors.accentLight,
                border: `1px solid ${darkTheme.colors.accent}`,
              }}
              title="Oben im App-Picker wählbar — zeigt das Feedback dieser App"
            >
              {appLabel(app)}
            </span>
          </h1>
          <p className="mt-1 text-xs text-text-tertiary">
            {list.data
              ? `${list.data.counts.new} neu · ${list.data.counts.triaged} in Bearbeitung · ${list.data.counts.resolved} erledigt`
              : 'Lädt …'}
          </p>
        </div>
      </header>

      <FilterBar
        status={status}
        onStatusChange={setStatus}
        source={source}
        onSourceChange={setSource}
        category={category}
        onCategoryChange={setCategory}
        search={search}
        onSearchChange={setSearch}
        counts={list.data?.counts}
      />

      <div className="mt-4 grid gap-4 lg:grid-cols-[3fr_4fr]">
        <ListPane
          items={list.data?.items ?? []}
          selectedId={selectedId}
          onSelect={setSelectedId}
          loading={list.isLoading}
          error={list.error}
        />
        <DetailPane id={selectedId} app={app} />
      </div>
    </div>
  );
}

// ── Filter Bar ────────────────────────────────────────────

interface FilterBarProps {
  status: FeedbackStatus | 'ALL';
  onStatusChange: (s: FeedbackStatus | 'ALL') => void;
  source: FeedbackSource | 'ALL';
  onSourceChange: (s: FeedbackSource | 'ALL') => void;
  category: FeedbackCategory | 'ALL';
  onCategoryChange: (c: FeedbackCategory | 'ALL') => void;
  search: string;
  onSearchChange: (v: string) => void;
  counts?: Record<FeedbackStatus, number>;
}

function FilterBar(props: FilterBarProps): JSX.Element {
  const statusPills: { value: FeedbackStatus | 'ALL'; label: string; count?: number }[] = [
    { value: 'ALL', label: 'Alle' },
    { value: 'new', label: 'Neu', count: props.counts?.new },
    { value: 'triaged', label: 'In Bearbeitung', count: props.counts?.triaged },
    { value: 'resolved', label: 'Erledigt', count: props.counts?.resolved },
    { value: 'wontfix', label: "Won't Fix", count: props.counts?.wontfix },
  ];

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md border p-3"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <input
        type="search"
        placeholder="Suche in Text/Email/Name…"
        value={props.search}
        onChange={(e) => props.onSearchChange(e.target.value)}
        className="flex-1 min-w-[220px] rounded-md border bg-bg-base px-3 py-1.5 text-sm text-text-primary outline-none focus:border-accent"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      />

      <div className="flex flex-wrap items-center gap-1.5">
        {statusPills.map((p) => (
          <Pill
            key={p.value}
            active={props.status === p.value}
            onClick={() => props.onStatusChange(p.value)}
            label={p.label}
            count={p.count}
          />
        ))}
      </div>

      <select
        value={props.source}
        onChange={(e) => props.onSourceChange(e.target.value as FeedbackSource | 'ALL')}
        className="rounded-md border bg-bg-base px-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      >
        <option value="ALL">Alle Quellen</option>
        <option value="APP">App</option>
        <option value="WEBSITE">Website</option>
      </select>

      <select
        value={props.category}
        onChange={(e) => props.onCategoryChange(e.target.value as FeedbackCategory | 'ALL')}
        className="rounded-md border bg-bg-base px-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      >
        <option value="ALL">Alle Kategorien</option>
        {(Object.keys(CATEGORY_LABEL) as FeedbackCategory[]).map((c) => (
          <option key={c} value={c}>
            {CATEGORY_LABEL[c]}
          </option>
        ))}
      </select>
    </div>
  );
}

function Pill({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full px-3 py-1 text-xs font-medium transition-colors"
      style={{
        background: active ? darkTheme.colors.accentSubtle : 'transparent',
        color: active ? darkTheme.colors.accentLight : darkTheme.colors.textSecondary,
        border: `1px solid ${active ? darkTheme.colors.accent : darkTheme.colors.borderSubtle}`,
      }}
    >
      {label}
      {count !== undefined && (
        <span className="ml-1.5 text-text-tertiary">{count}</span>
      )}
    </button>
  );
}

// ── List Pane ─────────────────────────────────────────────

interface ListPaneProps {
  items: FeedbackListItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
  error: unknown;
}

function ListPane({ items, selectedId, onSelect, loading, error }: ListPaneProps): JSX.Element {
  if (error) {
    return (
      <div
        className="rounded-md p-4 text-sm"
        style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
      >
        Fehler: {error instanceof Error ? error.message : 'unbekannt'}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-16 animate-pulse rounded-md"
            style={{ background: darkTheme.colors.bgSurface }}
          />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        className="rounded-md border p-8 text-center text-sm text-text-secondary"
        style={{
          background: darkTheme.colors.bgSurface,
          borderColor: darkTheme.colors.borderSubtle,
        }}
      >
        Keine Treffer.
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-md border"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
        {items.map((item) => (
          <ListRow
            key={item.id}
            item={item}
            selected={item.id === selectedId}
            onClick={() => onSelect(item.id)}
          />
        ))}
      </div>
    </div>
  );
}

function ListRow({
  item,
  selected,
  onClick,
}: {
  item: FeedbackListItem;
  selected: boolean;
  onClick: () => void;
}): JSX.Element {
  const statusInfo = STATUS_META[item.status];
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full border-b px-4 py-3 text-left transition-colors hover:bg-bg-elevated"
      style={{
        borderColor: darkTheme.colors.borderSubtle,
        background: selected ? darkTheme.colors.bgElevated : 'transparent',
        borderLeft: selected ? `3px solid ${darkTheme.colors.accent}` : '3px solid transparent',
      }}
    >
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className="rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
            style={{ background: statusInfo.bg, color: statusInfo.color }}
          >
            {statusInfo.label}
          </span>
          <span className="text-[11px] text-text-tertiary">
            {SOURCE_LABEL[item.source]} · {CATEGORY_LABEL[item.category]}
          </span>
        </div>
        <span className="text-[11px] text-text-tertiary">{formatRelative(item.createdAt)}</span>
      </div>
      <div className="line-clamp-2 text-sm font-medium text-text-primary">{item.message}</div>
      <div className="mt-1 flex items-center gap-2 text-[11px] text-text-secondary">
        {item.user ? (
          <span className="truncate">{item.user.email}</span>
        ) : item.email ? (
          <span className="truncate">{item.email}</span>
        ) : (
          <span className="text-text-tertiary">anonym</span>
        )}
        {item.npsScore !== null && (
          <span className="rounded bg-bg-base px-1 text-[10px]">NPS {item.npsScore}</span>
        )}
        {item.tags.slice(0, 2).map((t) => (
          <span
            key={t}
            className="rounded bg-bg-base px-1 text-[10px] text-text-tertiary"
          >
            {t}
          </span>
        ))}
      </div>
    </button>
  );
}

// ── Detail Pane ───────────────────────────────────────────

function DetailPane({ id, app }: { id: string | null; app: string }): JSX.Element {
  if (!id) {
    return (
      <div
        className="flex items-center justify-center rounded-md border p-12 text-sm text-text-tertiary"
        style={{
          background: darkTheme.colors.bgSurface,
          borderColor: darkTheme.colors.borderSubtle,
          minHeight: 400,
        }}
      >
        Wähle einen Eintrag aus der Liste.
      </div>
    );
  }
  return <DetailContent id={id} app={app} key={id} />;
}

function DetailContent({ id, app }: { id: string; app: string }): JSX.Element {
  const queryClient = useQueryClient();
  // Lesen ja, triagieren nein (CUR-995): Status, Tags und Notiz bleiben
  // sichtbar, sind fuer die Nur-Lesen-Rolle aber nicht bedienbar.
  const canWrite = useCanWrite();
  const { data, isLoading, error } = useQuery({
    queryKey: ['feedback', id, app],
    queryFn: () => fetchFeedbackDetail(id, app),
  });

  const updateMutation = useMutation({
    mutationFn: (patch: FeedbackUpdate) => updateFeedback(id, patch, app),
    onSuccess: (updated) => {
      queryClient.setQueryData(['feedback', id], updated);
      // Liste invalidieren, damit Status-Counter und Listen-Items frisch laden
      queryClient.invalidateQueries({ queryKey: ['feedback'] });
    },
  });

  const [noteDraft, setNoteDraft] = useState<string>('');
  const [tagInput, setTagInput] = useState('');

  if (isLoading) {
    return (
      <div
        className="rounded-md border p-6"
        style={{
          background: darkTheme.colors.bgSurface,
          borderColor: darkTheme.colors.borderSubtle,
        }}
      >
        <div className="h-4 w-1/3 animate-pulse rounded bg-bg-elevated" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="rounded-md p-4 text-sm"
        style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
      >
        Detail konnte nicht geladen werden.
      </div>
    );
  }

  const note = noteDraft.length > 0 || data.internalNote === null ? noteDraft : data.internalNote;

  function handleStatusChange(status: FeedbackStatus) {
    updateMutation.mutate({ status });
  }

  function handleAddTag(tag: string) {
    const trimmed = tag.trim();
    if (!trimmed) return;
    if (data!.tags.includes(trimmed)) return;
    updateMutation.mutate({ tags: [...data!.tags, trimmed] });
    setTagInput('');
  }

  function handleRemoveTag(tag: string) {
    updateMutation.mutate({ tags: data!.tags.filter((t) => t !== tag) });
  }

  function handleSaveNote() {
    // `readOnly` verhindert das Tippen, nicht den Fokus — `onBlur` feuert auch
    // fuer die Leserolle. Ohne diesen Riegel loest ein Klick ins Notizfeld und
    // wieder heraus einen PATCH aus, der als roter Fehler zurueckkommt
    // (CUR-995).
    if (!canWrite) return;
    updateMutation.mutate({ internalNote: note.trim() || null });
  }

  function copyAsBugReport() {
    const md = formatBugReport(data!);
    navigator.clipboard.writeText(md).catch(() => {});
  }

  return (
    <div
      className="flex flex-col gap-4 rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
              style={{
                background: STATUS_META[data.status].bg,
                color: STATUS_META[data.status].color,
              }}
            >
              {STATUS_META[data.status].label}
            </span>
            <span className="text-xs text-text-tertiary">
              {SOURCE_LABEL[data.source]} · {CATEGORY_LABEL[data.category]} ·{' '}
              {formatDate(data.createdAt)}
            </span>
          </div>
          {data.user && (
            <Link
              to={`/users/${data.user.id}`}
              className="mt-1 inline-block text-sm text-accent-light hover:underline"
            >
              {data.user.displayName ?? data.user.email}
            </Link>
          )}
          {!data.user && (data.email || data.name) && (
            <div className="mt-1 text-sm text-text-secondary">
              {data.name ? `${data.name} · ` : ''}
              {data.email}
            </div>
          )}
        </div>

        <select
          value={data.status}
          onChange={(e) => handleStatusChange(e.target.value as FeedbackStatus)}
          disabled={!canWrite || updateMutation.isPending}
          className="rounded-md border bg-bg-base px-2 py-1 text-xs text-text-primary outline-none focus:border-accent"
          style={{ borderColor: darkTheme.colors.borderSubtle }}
        >
          <option value="new">Neu</option>
          <option value="triaged">In Bearbeitung</option>
          <option value="resolved">Erledigt</option>
          <option value="wontfix">Won't Fix</option>
        </select>
      </div>

      {/* Tags */}
      <section>
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
          Tags
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {data.tags.map((t) =>
            canWrite ? (
              <button
                key={t}
                type="button"
                onClick={() => handleRemoveTag(t)}
                className="group rounded-full px-2 py-0.5 text-[11px]"
                style={{
                  background: darkTheme.colors.bgElevated,
                  color: darkTheme.colors.textPrimary,
                  border: `1px solid ${darkTheme.colors.borderSubtle}`,
                }}
                title="Klicken zum Entfernen"
              >
                {t} <span className="ml-1 text-text-tertiary group-hover:text-danger">×</span>
              </button>
            ) : (
              <span
                key={t}
                className="rounded-full px-2 py-0.5 text-[11px]"
                style={{
                  background: darkTheme.colors.bgElevated,
                  color: darkTheme.colors.textPrimary,
                  border: `1px solid ${darkTheme.colors.borderSubtle}`,
                }}
              >
                {t}
              </span>
            ),
          )}
          {canWrite && (
            <input
              type="text"
              placeholder="+ Tag (Enter)"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag(tagInput);
                }
              }}
              className="rounded-full border bg-bg-base px-2 py-0.5 text-[11px] text-text-primary outline-none focus:border-accent"
              style={{ borderColor: darkTheme.colors.borderSubtle, width: 120 }}
            />
          )}
        </div>
      </section>

      {/* Message */}
      <section>
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
          Nachricht
        </div>
        <p
          className="rounded-md p-3 text-sm leading-relaxed text-text-primary"
          style={{ background: darkTheme.colors.bgBase, whiteSpace: 'pre-wrap' }}
        >
          {data.message}
        </p>
      </section>

      {/* Strukturierte Daten / Context */}
      {(data.npsScore !== null || data.trigger || (data.context && Object.keys(data.context).length > 0)) && (
        <section>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
            Quelle
          </div>
          <div
            className="rounded-md p-3 text-xs"
            style={{ background: darkTheme.colors.bgBase, color: darkTheme.colors.textSecondary }}
          >
            {data.npsScore !== null && (
              <div>
                <strong className="text-text-primary">NPS:</strong> {data.npsScore}
              </div>
            )}
            {data.trigger && (
              <div>
                <strong className="text-text-primary">Trigger:</strong> {data.trigger}
              </div>
            )}
            {data.context && isBetaFeedback(data.context) && (
              <div className="mt-2">
                <BetaFeedbackContext ctx={data.context as BetaFeedbackShape} />
              </div>
            )}
            {data.context && (
              <details className="mt-2">
                <summary className="cursor-pointer">Rohdaten (JSON)</summary>
                <pre className="mt-1 overflow-auto font-mono text-[11px] text-text-primary">
                  {JSON.stringify(data.context, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </section>
      )}

      {/* Notiz */}
      <FeedbackReplyPanel key={`${app}:${id}`} feedback={data} app={app} canWrite={canWrite} />
      <section>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
            Notiz für Team
          </span>
          {data.triagedByUser && data.triagedAt && (
            <span className="text-[10px] text-text-tertiary">
              Zuletzt: {data.triagedByUser.email} · {formatRelative(data.triagedAt)}
            </span>
          )}
        </div>
        <textarea
          value={note}
          onChange={(e) => setNoteDraft(e.target.value)}
          onBlur={handleSaveNote}
          rows={3}
          readOnly={!canWrite}
          className="w-full rounded-md border bg-bg-base px-3 py-2 text-sm text-text-primary outline-none focus:border-accent read-only:opacity-70"
          style={{ borderColor: darkTheme.colors.borderSubtle }}
          placeholder={
            canWrite
              ? 'Markdown unterstützt. Speichert automatisch beim Verlassen des Feldes.'
              : 'Keine Notiz hinterlegt.'
          }
        />
      </section>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 border-t pt-3" style={{ borderColor: darkTheme.colors.borderSubtle }}>
        {data.user && (
          <Link
            to={`/users/${data.user.id}`}
            className="rounded-md border border-border-subtle bg-bg-elevated px-3 py-1.5 text-xs text-text-primary hover:bg-bg-overlay"
          >
            User öffnen
          </Link>
        )}
        <button
          type="button"
          onClick={copyAsBugReport}
          className="rounded-md border border-border-subtle bg-bg-elevated px-3 py-1.5 text-xs text-text-primary hover:bg-bg-overlay"
        >
          Als Bug-Report kopieren
        </button>
        {updateMutation.error && (
          <span className="text-xs text-danger">
            Fehler: {(updateMutation.error as Error).message}
          </span>
        )}
        {updateMutation.isPending && (
          <span className="text-xs text-text-tertiary">Speichert …</span>
        )}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────

function formatBugReport(item: import('./types').FeedbackDetail): string {
  return `## Feedback ${item.id}

**Quelle:** ${SOURCE_LABEL[item.source]}
**Kategorie:** ${CATEGORY_LABEL[item.category]}
**Status:** ${STATUS_META[item.status].label}
**Erstellt:** ${formatDate(item.createdAt)}
${item.user ? `**User:** ${item.user.email} (${item.user.id})` : ''}
${item.email && !item.user ? `**Email:** ${item.email}` : ''}
${item.npsScore !== null ? `**NPS:** ${item.npsScore}` : ''}
${item.trigger ? `**Trigger:** ${item.trigger}` : ''}

### Nachricht

${item.message}

${item.tags.length > 0 ? `### Tags\n${item.tags.join(', ')}` : ''}

${item.context ? `### Context\n\`\`\`json\n${JSON.stringify(item.context, null, 2)}\n\`\`\`` : ''}
${item.internalNote ? `### Interne Notiz\n${item.internalNote}` : ''}
`.replace(/\n{3,}/g, '\n\n');
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'gerade eben';
  if (ms < 3_600_000) return `vor ${Math.floor(ms / 60_000)} min`;
  if (ms < 86_400_000) return `vor ${Math.floor(ms / 3_600_000)} h`;
  return new Date(iso).toLocaleDateString('de-DE');
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE');
}
